import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, Star, Users, Zap, ArrowRight, Download, Upload, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { getPlans } from '../redux/slices/planSlice';
import { getWatermarkText } from '../redux/slices/settingsSlice';
import { generateInvoicePdf } from '../utils/generateInvoicePdf';

const Landing = () => {
  const dispatch = useDispatch();
  const { plans } = useSelector((state) => state.plan);
  const { watermarkText } = useSelector((state) => state.settings);
  const [guestInfo, setGuestInfo] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    client_info: {
      name: '',
      email: '',
      company: '',
    },
    shipTo: {
      name: '',
      email: '',
      company: '',
      address: '',
      phone: '',
      bankInfo: '',
    },
    billFrom: {
      name: '',
      email: '',
      company: '',
    },
    items: [{ description: '', quantity: 1, price: 0, total: 0 }],
    tax: { rate: 0, amount: 0 },
    discount: 0,
    shipping: 0,
    amountPaid: 0,
    balanceDue: 0,
    notes: '',
    terms: '',
    paymentTerms: '',
    poNumber: '',
    currency: 'USD',
  });

  const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';

  useEffect(() => {
    checkGuestLimit();
    dispatch(getPlans());
    dispatch(getWatermarkText());
    axios
      .post(`${API_URL}/traffic/hit`, {
        email: localStorage.getItem('ainix_guest_email') || '',
      })
      .catch(() => {});
  }, [dispatch]);

  const checkGuestLimit = async () => {
    try {
      const response = await axios.get(`${API_URL}/guest/limit`);
      setGuestInfo(response.data.guest_info);
    } catch (error) {
      console.error('Error checking guest limit:', error);
    }
  };

  const handleInputChange = (section, field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index][field] = value;

    if (field === 'quantity' || field === 'price') {
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].price;
    }

    setInvoiceData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, price: 0, total: 0 }]
    }));
  };

  const removeItem = (index) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const subtotal = invoiceData.items.reduce((sum, item) => sum + item.total, 0);
    const taxAmount = (subtotal * invoiceData.tax.rate) / 100;
    const total = subtotal + taxAmount - invoiceData.discount + invoiceData.shipping;
    const balanceDue = total - invoiceData.amountPaid;
    return { subtotal, taxAmount, total, balanceDue };
  };

  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [guestEmail, setGuestEmail] = useState(() => localStorage.getItem('ainix_guest_email') || '');
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);

  const handleLogoSelection = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setSelectedLogoFile(file);
    setLogoPreview(previewUrl);
    toast.success('Logo selected. Click "Upload Logo" to confirm.');
  };

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) {
      toast.error('Please select a logo file first');
      return;
    }

    const formData = new FormData();
    formData.append('logo', selectedLogoFile);

    try {
      const response = await fetch(`${API_URL}/upload/logo/guest`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setInvoiceData(prev => ({
          ...prev,
          billFrom: {
            ...prev.billFrom,
            logo: data.logoUrl
          }
        }));
        setSelectedLogoFile(null);
        setLogoPreview(null);
        toast.success('Logo uploaded successfully');
      } else {
        toast.error(data.message || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Failed to upload logo');
    }
  };

  const cancelLogoSelection = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setSelectedLogoFile(null);
    setLogoPreview(null);
  };

  const removeLogo = () => {
    setInvoiceData(prev => ({
      ...prev,
      billFrom: {
        ...prev.billFrom,
        logo: ''
      }
    }));
  };

  const persistGuestEmail = async (email, invoiceId) => {
    const response = await axios.post(`${API_URL}/guest/email`, { email, invoiceId });
    localStorage.setItem('ainix_guest_email', email);
    setGuestEmail(email);
    setGuestInfo(response.data.guest_info);
    return response.data.guest_info;
  };

  const completeGuestDownload = async (email) => {
    const sendTo = (email || guestEmail || guestInfo?.email || '').trim().toLowerCase();
    if (!sendTo || !sendTo.includes('@')) {
      setShowEmailCapture(true);
      return;
    }

    setIsCreating(true);
    try {
      const { subtotal, taxAmount, total, balanceDue } = calculateTotals();

      const response = await axios.post(`${API_URL}/guest/invoice`, {
        ...invoiceData,
        subtotal,
        tax: { ...invoiceData.tax, amount: taxAmount },
        total,
        balanceDue,
        guest_email: sendTo,
      });

      setGuestInfo(response.data.guest_info);
      const invoice = response.data.invoice;
      if (invoice?._id) {
        localStorage.setItem('ainix_guest_invoice_id', invoice._id);
        setPendingInvoiceId(invoice._id);
      }
      localStorage.setItem('ainix_guest_email', sendTo);
      setGuestEmail(sendTo);

      const { filename, base64 } = await generateInvoicePdf(invoice, {
        customBranding: false,
        apiUrl: API_URL,
        download: true,
      });

      try {
        const mailRes = await axios.post(`${API_URL}/guest/send-pdf`, {
          email: sendTo,
          invoiceId: invoice?._id,
          filename,
          pdfBase64: base64,
        });
        if (mailRes.data.emailed) {
          toast.success(`PDF downloaded and sent to ${sendTo}.`);
        } else {
          toast.error(
            `PDF downloaded. Email could not be sent: ${mailRes.data.reason || 'SMTP is not configured.'}`
          );
        }
      } catch (mailError) {
        toast.error(
          `PDF downloaded. Email could not be sent: ${mailError.response?.data?.message || mailError.message}`
        );
      }

      setInvoiceData({
        client_info: { name: '', email: '', company: '' },
        billFrom: { name: '', email: '', company: '' },
        items: [{ description: '', quantity: 1, price: 0, total: 0 }],
        tax: { rate: 0, amount: 0 },
        discount: 0,
        notes: '',
        terms: '',
        currency: 'USD',
      });
      setShowInvoiceForm(false);
      setShowEmailCapture(false);
    } catch (error) {
      if (error.response?.data?.email_required) {
        setShowEmailCapture(true);
        toast.error(error.response.data.message);
      } else if (error.response?.status === 429) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || 'Failed to create invoice');
      }
    } finally {
      setIsCreating(false);
      setEmailSaving(false);
    }
  };

  const handleSaveGuestEmail = async (e) => {
    e.preventDefault();
    if (!guestEmail.trim() || !guestEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setEmailSaving(true);
    try {
      await persistGuestEmail(guestEmail.trim(), pendingInvoiceId);
      await completeGuestDownload(guestEmail.trim());
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save email');
      setEmailSaving(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceData.client_info.name) {
      toast.error('Please enter client name');
      return;
    }

    if (invoiceData.items.length === 0 || !invoiceData.items[0].description) {
      toast.error('Please add at least one item');
      return;
    }

    const knownEmail = (guestEmail || guestInfo?.email || '').trim();
    if (!knownEmail) {
      setShowEmailCapture(true);
      return;
    }

    await completeGuestDownload(knownEmail);
  };

  const { subtotal, taxAmount, total, balanceDue } = calculateTotals();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-primary-600 mr-2" />
              <span className="text-2xl font-bold text-gray-900">Ainix Invoice</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900">
                Sign In
              </Link>
              <Link to="/register" className="btn btn-primary">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-primary-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Create Professional Invoices
            <span className="text-primary-600"> Instantly</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Generate beautiful, professional invoices in seconds. Enter your email to get the PDF — no account required.
          </p>
          
          {/* Guest Info */}
          {guestInfo && (
            <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto mb-8">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-500 mr-2" />
                <span className="font-medium text-gray-900">Guest Access</span>
              </div>
              <p className="text-gray-600 mb-2">
                You can create <strong>{guestInfo.remaining}</strong> more invoice(s) this month
              </p>
              <p className="text-sm text-gray-500">
                {guestInfo.invoices_created} of {guestInfo.monthly_limit} used
              </p>
              <p className="text-sm text-gray-500 mt-2">
                We email the PDF to you. Create an account to save invoices and clients.
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {guestInfo?.can_create ? (
              <button
                onClick={() => setShowInvoiceForm(true)}
                className="btn btn-primary text-lg px-8 py-4 flex items-center justify-center"
              >
                <FileText className="w-6 h-6 mr-2" />
                Create Free Invoice
              </button>
            ) : (
              <Link to="/register" className="btn btn-primary text-lg px-8 py-4 flex items-center justify-center">
                <ArrowRight className="w-6 h-6 mr-2" />
                Sign Up for Unlimited Access
              </Link>
            )}
            <Link to="/register" className="btn btn-secondary text-lg px-8 py-4">
              View All Plans
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything you need to manage invoices
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Professional invoice generation with advanced features for businesses of all sizes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Lightning Fast</h3>
              <p className="text-gray-600">
                Create professional invoices in under 30 seconds with our intuitive interface.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">PDF Export</h3>
              <p className="text-gray-600">
                Download your invoices as professional PDF documents ready for printing or emailing.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Client Management</h3>
              <p className="text-gray-600">
                Keep track of all your clients and their invoice history in one place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-600">
              Choose the plan that's right for your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={plan._id}
                className={`bg-white rounded-lg shadow-md p-8 relative ${
                  plan.name.toLowerCase().includes('pro') ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                {plan.name.toLowerCase().includes('pro') && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-primary-600">
                    ${plan.price}
                  </span>
                  <span className="text-lg text-gray-600">/{plan.interval}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <span>
                      {plan.features.invoicesPerMonth === -1 
                        ? 'Unlimited invoices' 
                        : `${plan.features.invoicesPerMonth} invoices/month`}
                    </span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <span>PDF export</span>
                  </li>
                  {plan.features.customBranding && (
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span>Custom branding</span>
                    </li>
                  )}
                  {plan.features.clientManagement && (
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                      <span>Client management</span>
                    </li>
                  )}
                </ul>

                {plan.price === 0 ? (
                  <Link to="/register" className="btn btn-secondary w-full">
                    Get Started
                  </Link>
                ) : (
                  <Link to="/register" className="btn btn-primary w-full">
                    Get Started
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Invoice Form Modal */}
      {showInvoiceForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Create Invoice</h2>
                <button
                  onClick={() => setShowInvoiceForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Bill From & Bill To */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Bill From</h3>
                  
                  {/* Logo Upload Section */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                    {invoiceData.billFrom.logo ? (
                      <div className="relative inline-block">
                        <img 
                          src={`${API_URL.replace('/api', '')}${invoiceData.billFrom.logo}`} 
                          alt="Company Logo" 
                          className="w-16 h-16 object-contain border border-gray-300 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : logoPreview ? (
                      <div className="space-y-2">
                        <div className="relative inline-block">
                          <img 
                            src={logoPreview} 
                            alt="Logo Preview" 
                            className="w-16 h-16 object-contain border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={handleLogoUpload}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          >
                            Upload
                          </button>
                          <button
                            type="button"
                            onClick={cancelLogoSelection}
                            className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoSelection}
                          className="hidden"
                          id="guest-logo-upload"
                        />
                        <label htmlFor="guest-logo-upload" className="cursor-pointer">
                          <ImageIcon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-600">Select logo</p>
                          <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Your Name"
                      className="input"
                      value={invoiceData.billFrom.name}
                      onChange={(e) => handleInputChange('billFrom', 'name', e.target.value)}
                    />
                    <input
                      type="email"
                      placeholder="your@email.com"
                      className="input"
                      value={invoiceData.billFrom.email}
                      onChange={(e) => handleInputChange('billFrom', 'email', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Company"
                      className="input"
                      value={invoiceData.billFrom.company}
                      onChange={(e) => handleInputChange('billFrom', 'company', e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Bill To</h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Client Name *"
                      className="input"
                      value={invoiceData.client_info.name}
                      onChange={(e) => handleInputChange('client_info', 'name', e.target.value)}
                      required
                    />
                    <input
                      type="email"
                      placeholder="client@email.com"
                      className="input"
                      value={invoiceData.client_info.email}
                      onChange={(e) => handleInputChange('client_info', 'email', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Company"
                      className="input"
                      value={invoiceData.client_info.company}
                      onChange={(e) => handleInputChange('client_info', 'company', e.target.value)}
                    />
                    <textarea
                      placeholder="Address (Street, City, State, ZIP, Country)"
                      className="input resize-none"
                      rows="3"
                      value={invoiceData.client_info.address || ''}
                      onChange={(e) => handleInputChange('client_info', 'address', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Ship To (Optional) */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Ship To (Optional)</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Name"
                      className="input"
                      value={invoiceData.shipTo.name}
                      onChange={(e) => handleInputChange('shipTo', 'name', e.target.value)}
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      className="input"
                      value={invoiceData.shipTo.email}
                      onChange={(e) => handleInputChange('shipTo', 'email', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Company"
                      className="input"
                      value={invoiceData.shipTo.company}
                      onChange={(e) => handleInputChange('shipTo', 'company', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Phone"
                      className="input"
                      value={invoiceData.shipTo.phone}
                      onChange={(e) => handleInputChange('shipTo', 'phone', e.target.value)}
                    />
                  </div>
                  <textarea
                    placeholder="Shipping Address (Street, City, State, ZIP, Country)"
                    className="input resize-none"
                    rows="3"
                    value={invoiceData.shipTo.address}
                    onChange={(e) => handleInputChange('shipTo', 'address', e.target.value)}
                  />
                  <textarea
                    placeholder="Bank Information (Account details, SWIFT, IBAN, etc.)"
                    className="input resize-none"
                    rows="2"
                    value={invoiceData.shipTo.bankInfo}
                    onChange={(e) => handleInputChange('shipTo', 'bankInfo', e.target.value)}
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Items</h3>
                  <button
                    type="button"
                    onClick={addItem}
                    className="btn btn-secondary btn-sm"
                  >
                    Add Item
                  </button>
                </div>

                <div className="space-y-3">
                  {invoiceData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-3 items-center">
                      <input
                        type="text"
                        placeholder="Description"
                        className="input col-span-5"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        className="input col-span-2"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        min="1"
                      />
                      <input
                        type="number"
                        placeholder="Price"
                        className="input col-span-2"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                      />
                      <div className="col-span-2 font-medium">${item.total.toFixed(2)}</div>
                      {invoiceData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="col-span-1 text-red-600 hover:text-red-700"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="mt-6 pt-6 border-t">
                  <div className="space-y-2 max-w-xs ml-auto">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Tax (%):</span>
                      <input
                        type="number"
                        className="input w-20 text-sm"
                        value={invoiceData.tax.rate}
                        onChange={(e) => setInvoiceData(prev => ({
                          ...prev,
                          tax: { ...prev.tax, rate: parseFloat(e.target.value) || 0 }
                        }))}
                        min="0"
                        step="0.1"
                      />
                      <span className="font-medium">${taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Discount:</span>
                      <input
                        type="number"
                        className="input w-32 text-sm"
                        value={invoiceData.discount}
                        onChange={(e) => setInvoiceData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  className="input"
                  rows="3"
                  placeholder="Additional notes..."
                  value={invoiceData.notes}
                  onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>

            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                onClick={() => setShowInvoiceForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateInvoice}
                disabled={isCreating}
                className="btn btn-primary"
              >
                {isCreating ? 'Creating...' : 'Create & Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailCapture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900">Save this invoice — enter email</h3>
            <p className="text-sm text-gray-600 mt-2">
              We will download the PDF and send a copy to this address. No password or account required.
            </p>
            <form onSubmit={handleSaveGuestEmail} className="mt-4 space-y-3">
              <input
                type="email"
                className="input w-full"
                placeholder="you@email.com"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary w-full" disabled={emailSaving || isCreating}>
                {emailSaving || isCreating ? 'Sending...' : 'Send PDF to this email'}
              </button>
            </form>
            <Link
              to="/register"
              className="block text-center text-sm text-primary-600 hover:text-primary-700 mt-3"
            >
              Create account to keep this invoice
            </Link>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-primary-400 mr-2" />
              <span className="text-2xl font-bold">Ainix Invoice</span>
            </div>
            <p className="text-gray-400 mb-4">
              Professional invoice generation made simple
            </p>
            <div className="flex justify-center space-x-6">
              <Link to="/login" className="text-gray-400 hover:text-white">
                Sign In
              </Link>
              <Link to="/register" className="text-gray-400 hover:text-white">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;

